// app/features/ai-workflows/services_and_hooks/personalizedUseCasesStore.ts
// app/features/personalized-use-cases/services_and_hooks/personalizedUseCasesStore.ts
// features/personalized-use-cases/services_and_hooks/personalizedUseCasesStore.ts

import { create } from "zustand";
import { persist } from "zustand/middleware";

import { Database } from "@/types/supabase";

import {
  CreationStep,
  GenerationStatus,
  ProblemSubmissionData,
} from "../personalizedUseCasesTypes";

// Use Supabase types
type Organization = Database["public"]["Tables"]["organizations"]["Row"];
// Use the correct type for generated use cases
type PersonalizedUseCase =
  Database["public"]["Tables"]["personalized_use_cases"]["Row"];

// This interface defines the complete state for this feature's store
interface PersonalizedUseCasesState {
  // Current step in the creation flow
  currentStep: CreationStep;
  setCurrentStep: (step: CreationStep) => void;

  // Selected organization
  selectedOrganization: Organization | null;
  setSelectedOrganization: (org: Organization | null) => void;

  // Problem description and context
  problemData: Partial<ProblemSubmissionData>;
  updateProblemData: (data: Partial<ProblemSubmissionData>) => void;
  clearProblemData: () => void;

  // Generation status
  generationStatus: GenerationStatus;
  setGenerationStatus: (status: GenerationStatus) => void;

  // Generated use case - should be PersonalizedUseCase
  generatedUseCase: PersonalizedUseCase | null;
  setGeneratedUseCase: (useCase: PersonalizedUseCase | null) => void;

  // Reset the entire process
  resetFlow: () => void;

  // View mode ('list' or 'create')
  viewMode: "list" | "create";
  setViewMode: (mode: "list" | "create") => void;
}

// Initial state
const initialState = {
  currentStep: "organization_selection" as CreationStep,
  selectedOrganization: null,
  problemData: {},
  generationStatus: "not_started" as GenerationStatus,
  generatedUseCase: null,
  viewMode: "list" as "list" | "create", // Default view mode
};

export const usePersonalizedUseCasesStore = create<PersonalizedUseCasesState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentStep: (step) => set({ currentStep: step }),

      setSelectedOrganization: (org) =>
        set({
          selectedOrganization: org,
          // Automatically update the organizationId in problemData
          problemData: org
            ? {
                ...get().problemData,
                organizationId: org.id,
              }
            : get().problemData,
        }),

      updateProblemData: (data) =>
        set((state) => ({
          problemData: { ...state.problemData, ...data },
        })),

      // clearProblemData: () => set({ problemData: {} }),
      clearProblemData: () => {
        console.log("[Store] Clearing problemData");
        set({ problemData: initialState.problemData }); // Reset to initial empty object
      },

      setGenerationStatus: (status) => set({ generationStatus: status }),

      setGeneratedUseCase: (useCase) => set({ generatedUseCase: useCase }),

      // Enhanced resetFlow with better logging
      resetFlow: () => {
        const currentViewMode = get().viewMode;
        const currentStep = get().currentStep;
        const caller = new Error().stack?.split("\n")[2] || "unknown";

        console.log("🔥 RESETTING FLOW:");
        console.log("- Called from:", caller);
        console.log("- Current viewMode:", currentViewMode);
        console.log("- Current step:", currentStep);
        console.log(
          "- Current problemData:",
          JSON.stringify(get().problemData),
        );

        set({
          ...initialState,
          viewMode: currentViewMode, // Preserve the current view mode
          currentStep: "organization_selection",
          selectedOrganization: null,
          problemData: {},
          generationStatus: "not_started",
          generatedUseCase: null,
        });

        // Verify the reset worked
        console.log("✅ AFTER RESET:");
        console.log("- viewMode preserved:", get().viewMode);
        console.log("- step reset to:", get().currentStep);
        console.log(
          "- problemData emptied:",
          JSON.stringify(get().problemData),
        );
      },

      // Add the setter for viewMode
      setViewMode: (mode) => set({ viewMode: mode }),
    }),
    {
      name: "personalized-use-cases-storage", // name of the item in the storage (must be unique)
      // Optionally configure storage (e.g., localStorage)
      // storage: createJSONStorage(() => sessionStorage), // (optional) by default, 'localStorage' is used
    },
  ),
);
